import { startWorkstation, stopWorkstation } from '@/api/cloudFunctionWrappers'
import { errorIfInvalidResponse } from '@/api/errors'
import { WorkstationDocument } from '@/api/types/workstation'
import {
  apiChangeWorkstationCodename,
  apiChangeWorkstationName,
  apiCreateSingleWorkstation,
  apiGetShareeWorkstationsUUIDs,
  apiModifyRootVolume,
  deleteWorkstation,
  getTeradiciConnection,
  getUsageTimeData,
} from '@/api/workstations'
import { connectorApiUrl } from '@/config'
import { db } from '@/db'
import router from '@/router'
import { ensureCurrentUser } from '@/services/auth'
import { generateWorkstationName } from '@/services/names'
import { routerPush } from '@/services/routing'
import { getExistingResources } from '@/services/selectors'
import { sortAlphabeticallyByAProperty } from '@/services/sorting'
import {
  trackWorkstationConnectRdpAttempt,
  trackWorkstationConnectRdpFailure,
  trackWorkstationConnectRdpSuccess,
  trackWorkstationConnectTgxAttempt,
  trackWorkstationConnectTgxFailure,
  trackWorkstationConnectTgxSuccess,
  trackWorkstationDeleteAttempt,
  trackWorkstationStartAttempt,
  trackWorkstationStopAttempt,
} from '@/services/tracking'
import { RequestInfo, RequestStatus, RootState } from '@/store/types/root'
import {
  WorkstationRequestName,
  WorkstationRequests,
  WorkstationRequestUpdate,
  WorkstationState,
  WorkstationUsageTime,
} from '@/store/types/workstation'
import protocolDetection from 'custom-protocol-detection'
import { ActionTree, Commit, Dispatch, GetterTree, Module, MutationTree } from 'vuex'
import { firestoreAction } from 'vuexfire'
import { WorkstationTypes } from '../types/config'

const getDefaultState = (): WorkstationState => {
  return {
    ownerWorkstations: null,
    shareeWorkstations: null,
    workstationsRequests: {},
    usage: null,
  }
}

const moduleState = getDefaultState()

// reasoning for doing it ourside actions:
// 1. handyness. Actions by themselves accept only a singular payload
export const updateWorkstationRequest = (
  commit: Commit,
  dispatch: Dispatch,
  uuid: string,
  name: WorkstationRequestName,
  status: RequestStatus,
  notificationContent?: string,
) => {
  if (!moduleState.workstationsRequests || !moduleState.workstationsRequests[uuid]) {
    commit('CREATE_WORKSTATION_REQUEST_MAP', uuid)
  }
  const error = status === 'failed' ? notificationContent : undefined
  commit('SET_WORKSTATION_REQUEST_INFO', new WorkstationRequestUpdate(uuid, name, status, error))
  if (notificationContent) {
    if (status === 'successful' || status === 'pending') {
      dispatch('notification/success', notificationContent, { root: true })
    } else if (status === 'failed') {
      dispatch('notification/error', notificationContent, { root: true })
    }
  }
}

export const initWorkstationRequestHelper = (
  commit: Commit,
  dispatch: Dispatch,
  uuid: string,
  name: WorkstationRequestName,
  optionalPendingNotificationText?: string,
) => {
  updateWorkstationRequest(commit, dispatch, uuid, name, 'pending', optionalPendingNotificationText)
  const setSuccess = (notificationText?: string) =>
    updateWorkstationRequest(commit, dispatch, uuid, name, 'successful', notificationText)
  const setFailure = (error?: string) =>
    updateWorkstationRequest(commit, dispatch, uuid, name, 'failed', error)
  return [setSuccess, setFailure]
}

export const moduleGetters: GetterTree<WorkstationState, RootState> = {
  workstations(state) {
    if (!state.ownerWorkstations || !state.shareeWorkstations) {
      // null is used as a sentinel value for the loading state.
      return null
    }
    let workstationsToShow: WorkstationDocument[] = []
    if (state.ownerWorkstations !== null && state.ownerWorkstations.length > 0) {
      workstationsToShow = workstationsToShow.concat(state.ownerWorkstations)
    }
    if (state.shareeWorkstations !== null && state.shareeWorkstations.length > 0) {
      workstationsToShow = workstationsToShow.concat(state.shareeWorkstations)
    }
    // a bit of hack to ensure the workstations possess the necessary methods
    const workstations = (getExistingResources(workstationsToShow) as WorkstationDocument[]).map(
      (ws) => new WorkstationDocument({ ...ws }),
    )
    sortAlphabeticallyByAProperty(workstations)
    return workstations
  },
  hasPendingRequests:
    (state) =>
    (uuid: string): boolean => {
      if (state.workstationsRequests && state.workstationsRequests[uuid]) {
        return (
          state.workstationsRequests[uuid].stopWorkstation.pending ||
          state.workstationsRequests[uuid].startWorkstation.pending ||
          state.workstationsRequests[uuid].deleteWorkstation.pending
        )
      }
      return false
    },
  requestInfo:
    (state) =>
    (uuid: string): WorkstationRequests | undefined => {
      if (state.workstationsRequests && state.workstationsRequests[uuid]) {
        return state.workstationsRequests[uuid]
      }
      return undefined
    },
  info:
    (state, getters) =>
    (uuid: string): WorkstationDocument | {} => {
      if (!state.ownerWorkstations && !state.shareeWorkstations) {
        return {}
      }
      const found = getters.workstations.find(
        (workstation: WorkstationDocument) => workstation.uuid === uuid,
      )
      if (found) {
        // TODO This convertion shouldn't be needed here if workstations were properly typed already
        return new WorkstationDocument({ ...found })
      }
      return {}
    },
  canStop:
    (_state, getters) =>
    (uuid: string): boolean => {
      if (getters.hasPendingRequests(uuid)) {
        return false
      }
      const w = getters.info(uuid)
      if (w.setupInProgress()) {
        return false
      }
      const { uid } = ensureCurrentUser()
      if (
        w.block_stoppage_access &&
        w.block_stoppage_access === true &&
        !(uid === w.owner_id || uid === w.last_started_by_user_id)
      ) {
        return false
      }
      return w.status === 'running'
    },
  canStart:
    (_state, getters) =>
    (uuid: string): boolean => {
      if (getters.hasPendingRequests(uuid)) {
        return false
      }
      const w = getters.info(uuid)
      if (w.setupInProgress()) {
        return false
      }
      return w.status === 'stopped'
    },
  canConnect:
    (_state, getters) =>
    (uuid: string): boolean => {
      if (getters.hasPendingRequests(uuid)) {
        return false
      }
      const w = getters.info(uuid)
      if (w.setupInProgress()) {
        return false
      }
      return w.status === 'running'
    },
  canAssign:
    (_state, getters, _rootState, rootGetters) =>
    (uuid: string): boolean => {
      const { uid } = ensureCurrentUser()
      const workstationInfo = getters.info(uuid)
      const isWorkstationOwner = uid === workstationInfo.owner_id
      return rootGetters['user/hasTeamsEnabled'] && isWorkstationOwner
    },
  canBlockStoppage:
    (_state, getters, _rootState, rootGetters) =>
    (uuid: string): boolean => {
      const { uid } = ensureCurrentUser()
      const workstationInfo = getters.info(uuid)
      if (
        workstationInfo.status == 'running' &&
        workstationInfo.block_stoppage_access &&
        workstationInfo.block_stoppage_access === true &&
        !(uid === workstationInfo.owner_id || uid === workstationInfo.last_started_by_user_id)
      ) {
        return true
      }
      return false
    },
  tgxConnectionRequestInfo:
    (state) =>
    (uuid: string): RequestInfo => {
      return state.workstationsRequests[uuid]?.connectToWorkstationTGX
    },
  browserConnectionInProgress: (state) => (uuid: string) => {
    return !!state.workstationsRequests[uuid]?.connectToWorkstationRDP.pending
  },
  errored:
    (_state, getters) =>
    (uuid: string): boolean => {
      const w = getters.info(uuid)
      return w.status === 'error'
    },
  displayError:
    (_state, getters) =>
    (uuid: string): string => {
      const w = getters.info(uuid)
      return w.display_error
    },
  setupInProgress:
    (_state, getters) =>
    (uuid: string): boolean => {
      const w = getters.info(uuid)
      return !w.display_error ? w.setupInProgress() : false
    },
  statusInProgress:
    (_state, getters) =>
    (uuid: string): boolean => {
      const freeze = getters.hasPendingRequests(uuid)
      const w = getters.info(uuid)
      // When do we want to show the spinner next to status?
      return freeze || w.status === 'pending' || w.status === 'stopping' || w.setupInProgress()
    },
  displayStatus:
    (state, getters) =>
    (uuid: string): string => {
      if (state.workstationsRequests[uuid]?.startWorkstation.pending) {
        return 'pending'
      }
      if (state.workstationsRequests[uuid]?.stopWorkstation.pending) {
        return 'stopping'
      }
      const w = getters.info(uuid)
      if (w.display_error) return 'error'
      if (w.setupInProgress()) {
        return 'preparing'
      }
      return w.status
    },
  ownsAny(state): boolean {
    const isLoaded = Array.isArray(state.ownerWorkstations)
    if (!isLoaded) return false
    return state.ownerWorkstations!.some((ws) => !ws.deleted)
  },
  // get number of the owned, not-deleted workstations
  numberOfOwnerWorkstations(state): number {
    const { ownerWorkstations } = state
    return getExistingResources(ownerWorkstations).length
  },
  hasAnyPendingCreationRequests(state): boolean {
    const workstationCreatePendingRequests = Object.values(state.workstationsRequests).map(
      (wsReq: WorkstationRequests) => wsReq.createWorkstation.pending,
    )
    return workstationCreatePendingRequests.some((isPending: boolean) => isPending)
  },
  hasRunningWorkstations(state): boolean {
    if (!state.ownerWorkstations) {
      return false
    }
    return Boolean(
      (getExistingResources(state.ownerWorkstations) as WorkstationDocument[]).find(
        (w: WorkstationDocument) =>
          ['preparing', 'rebooting', 'running', 'pending', 'stopping'].includes(w.status || ''),
      ),
    )
  },

  // for surveying
  pendingStopRequestsWorkstationsUuids(state): string[] {
    // RETURN STRING ARRAY
    const successfullyStoppedWorkstationUuids: string[] = []
    Object.entries(state.workstationsRequests).forEach(
      ([uuid, wsReqest]: [string, WorkstationRequests]) => {
        if (wsReqest.stopWorkstation.pending) {
          successfullyStoppedWorkstationUuids.push(uuid)
        }
      },
    )

    return successfullyStoppedWorkstationUuids
  },

  hoursRemaining(state): number {
    return state.usage?.hoursRemaining || 0
  },

  totalHoursAvailable(state): number {
    return state.usage?.totalHours || 0
  },

  cycleResetTimestamp(state): number {
    return state.usage?.cycleResetTimestamp || 0
  },

  hasHoursRemaining(_state, getters): boolean {
    return !!getters.hoursRemaining
  },
}

export const moduleActions: ActionTree<WorkstationState, RootState> = {
  async connectRDP({ commit, dispatch, rootGetters }, workstation: WorkstationDocument) {
    const [setSuccess, setFailure] = initWorkstationRequestHelper(
      commit,
      dispatch,
      workstation.uuid,
      'connectToWorkstationRDP',
    )
    const profile = rootGetters['user/profile']
    trackWorkstationConnectRdpAttempt(profile, workstation.uuid, workstation.name)
    try {
      const { port, username, password } = workstation
      const data = {
        hostname: workstation.getRDPHostname(),
        port,
        username,
        password,
      }
      const response = await fetch(`${connectorApiUrl}/connect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      await errorIfInvalidResponse(response, 'Failed to connect')
      const json = await response.json()
      window.open(json.redirect)
      trackWorkstationConnectRdpSuccess(profile, workstation.uuid, workstation.name)
      setSuccess('Connected!')
    } catch (err: any) {
      trackWorkstationConnectRdpFailure(profile, workstation.uuid, workstation.name)
      setFailure(err.message)
      throw err
    }
  },
  async connectTGX({ commit, dispatch, rootGetters }, workstation: WorkstationDocument) {
    const [setSuccess, setFailure] = initWorkstationRequestHelper(
      commit,
      dispatch,
      workstation.uuid,
      'connectToWorkstationTGX',
    )
    const profile = rootGetters['user/profile']
    trackWorkstationConnectTgxAttempt(profile, workstation.uuid, workstation.name)
    try {
      const { port, username, password } = workstation
      const data = {
        workstation_uuid: workstation.uuid,
        hostname: workstation.getTGXHostname(),
        port,
        username,
        password,
      }
      const controller = new AbortController()
      const responsePromise = fetch(`${connectorApiUrl}/connect-tgx`, {
        method: 'POST',
        redirect: 'follow',
        body: JSON.stringify(data),
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
        },
      })
      // These two values below are the same as in connector API
      const numberOfRetiresTgx = 4
      const delayBetweenRetry = 7.5
      // Extra time it may take for the request to come back in case there were 4 attempts
      const extraTime = 3.0
      const timeToAbortRequest = (numberOfRetiresTgx * delayBetweenRetry + extraTime) * 1000
      setTimeout(() => controller.abort(), timeToAbortRequest)
      try {
        const response = await responsePromise
        await errorIfInvalidResponse(response, 'Failed to connect TGX')
        const json = await response.json()
        // HACK: we need the custom-protocol-detection only the part
        // that opens the URL with the hidden iframe and opens the URL from it.
        // We don't use its functionality to detect if the app is registered in the OS
        // as it turned out to be unreliable (it was redirecting to install page even if the app was installed,
        // and it was not redirecting to install page when app was not installed).
        protocolDetection(
          json.redirect,
          () => {},
          () => {},
          () => {},
        )
        trackWorkstationConnectTgxSuccess(profile, workstation.uuid, workstation.name)
        setSuccess('Opening Renderro Client...')
      } catch (err: any) {
        // In general the error here is "User aborted the request"
        // It should only happen when Connector API is unreachable
        // (e.g. during deployment). Normally the request comes back after up to 30 seconds + epsilon time
        // with error message from connector.
        throw new Error('Connection failed. Please try again.')
      }
    } catch (err: any) {
      trackWorkstationConnectTgxFailure(profile, workstation.uuid, workstation.name)
      setFailure(err.message)
      throw err
    }
  },
  async connectTeradici({ commit, dispatch }, workstationUuid: string) {
    const [setSuccess, setFailure] = initWorkstationRequestHelper(
      commit,
      dispatch,
      workstationUuid,
      'connectToWorkstationTeradici',
    )
    try {
      const controller = new AbortController()
      const responsePromise = getTeradiciConnection(workstationUuid, controller.signal)
      // These two values below are the same as in connector API
      const numberOfRetiresTeradici = 4
      const delayBetweenRetry = 7.5
      // Extra time it may take for the request to come back in case there were 4 attempts
      const extraTime = 3.0
      const timeToAbortRequest = (numberOfRetiresTeradici * delayBetweenRetry + extraTime) * 1000
      setTimeout(() => controller.abort(), timeToAbortRequest)
      try {
        const response = await responsePromise
        await errorIfInvalidResponse(response, 'Failed to connect Teradici')
        const json = await response.json()
        // HACK: we need the custom-protocol-detection only the part
        // that opens the URL with the hidden iframe and opens the URL from it.
        // We don't use its functionality to detect if the app is registered in the OS
        // as it turned out to be unreliable (it was redirecting to install page even if the app was installed,
        // and it was not redirecting to install page when app was not installed).
        protocolDetection(
          json.redirect,
          () => {},
          () => {},
          () => {},
        )
        setSuccess('Opening Renderro Client...')
      } catch (err: any) {
        // In general the error here is "User aborted the request"
        // It should only happen when Connector API is unreachable
        // (e.g. during deployment). Normally the request comes back after up to 30 seconds + epsilon time
        // with error message from connector.
        throw new Error('Connection failed. Please try again.')
      }
      setSuccess('Connected!')
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },
  async start({ commit, dispatch, getters, rootGetters }, { uuid, codename, name }) {
    const [setSuccess, setFailure] = initWorkstationRequestHelper(
      commit,
      dispatch,
      uuid,
      'startWorkstation',
      'Computer is starting...',
    )
    const info = getters.info(uuid)
    const profile = rootGetters['user/profile']
    trackWorkstationStartAttempt(profile, uuid, info.name)
    try {
      await apiChangeWorkstationCodename(uuid, codename)
      await startWorkstation(uuid)
      setSuccess()
    } catch (err: any) {
      setFailure(err)
      throw err
    }
  },
  async stop({ commit, dispatch, getters, rootGetters }, { uuid }) {
    const [setSuccess, setFailure] = initWorkstationRequestHelper(
      commit,
      dispatch,
      uuid,
      'stopWorkstation',
      'Computer is stopping...',
    )
    const info = getters.info(uuid)
    const profile = rootGetters['user/profile']
    trackWorkstationStopAttempt(profile, uuid, info.name)
    try {
      await stopWorkstation(uuid)
      dispatch('user/ensureProfile', undefined, { root: true })
      setSuccess('Computer stopped!')
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },
  async create({ commit, dispatch, rootGetters }) {
    const name = generateWorkstationName()

    const workstationTypes: WorkstationTypes = rootGetters['config/workstationTypes']
    // NOTE HACK Filtering here to grab the first selectable workstation
    // to avoid picking g4-test-...
    // It should pick first g5 instance so if they are ordered correctly that will be G5 Basic type
    const codename = workstationTypes.filter((w) => w.selectable)[0].codename

    // when creating workstation the uuid is not known
    // until connector creates a firestore document
    // assign pseudo-uuid for request tracking purposes
    const unknownUuid = `${name} ${codename}`

    const [setSuccess, setFailure] = initWorkstationRequestHelper(
      commit,
      dispatch,
      unknownUuid,
      'createWorkstation',
    )
    try {
      await apiCreateSingleWorkstation(name, codename)
      setSuccess(`${name} created!`)
      routerPush(router, '/computers')
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },
  async delete({ commit, dispatch, getters, rootGetters }, { uuid }) {
    const [setSuccess, setFailure] = initWorkstationRequestHelper(
      commit,
      dispatch,
      uuid,
      'deleteWorkstation',
    )
    const info = getters.info(uuid)
    const profile = rootGetters['user/profile']
    trackWorkstationDeleteAttempt(profile, uuid, info.name)
    try {
      await deleteWorkstation(uuid)
      setSuccess('Computer deleted!')
      routerPush(router, '/computers')
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },
  async ensureWorkstations({ getters, dispatch }) {
    if (getters.workstations === null) {
      await dispatch('bindUserWorkstations')
    }
  },

  async bindUserWorkstations({ dispatch }) {
    /**
     *  Why are we having these two alongside?
     *
     * The most important thing here is real-time binding with firestore.
     * Connector endpoints returns uuids of machines shared with our user.
     * To get uuids of machines owned, we would have to query the firestore.
     * We are getting the two together in the workstations getter.
     */
    return Promise.all([dispatch('bindOwnerWorkstations'), dispatch('bindShareeWorkstations')])
  },

  bindOwnerWorkstations: firestoreAction(({ bindFirestoreRef, state }) => {
    // avoid rebinding when unnecessary
    // we want to synch with user's workstations, that are not deleted:
    if (state.ownerWorkstations !== null) return
    const currentUser = ensureCurrentUser()

    return bindFirestoreRef(
      'ownerWorkstations',
      db.collection('workstations').where('owner_id', '==', currentUser.uid),
      { wait: true },
    )
  }),

  bindShareeWorkstations: firestoreAction(async ({ bindFirestoreRef, commit }) => {
    // not avoiding rebounding - with every call we want binding with new uuidList

    const uuidAndUsersInfoList = await apiGetShareeWorkstationsUUIDs()
    const uuidList = uuidAndUsersInfoList.map((obj: any) => obj.uuid)

    // the vuexfire library disallows binding with 'in' opertator on empty array,
    // so we manually set shareeWorkstations to empty array if no shared drives are returned from the API.
    if (uuidList.length) {
      return bindFirestoreRef(
        'shareeWorkstations',
        db.collection('workstations').where('uuid', 'in', uuidList),
        // .where('deleted', '==', '')
        { wait: true },
      )
    }
    return commit('SET_SHAREE_WORKSTATIONS', [])
  }),

  async setUsageData({ commit }) {
    const workstationUsageTimeData = await getUsageTimeData()
    commit('SET_USAGE_DATA', workstationUsageTimeData)
  },
  async resetWorkstationState({ commit }) {
    commit('resetState')
  },
  async changeWorkstationName({ commit, dispatch, getters, rootGetters }, { uuid, name }) {
    const [setSuccess, setFailure] = initWorkstationRequestHelper(
      commit,
      dispatch,
      uuid,
      'changeWorkstationName',
    )
    try {
      await apiChangeWorkstationName(uuid, name)
      setSuccess('Name changed!')
    } catch (err: any) {
      setFailure(err)
      throw err
    }
  },
  async modifyRootVolume(
    { commit, dispatch, getters, rootGetters },
    { uuid, root_volume_increment_size_gb, root_volume_throughput },
  ) {
    const [setSuccess, setFailure] = initWorkstationRequestHelper(
      commit,
      dispatch,
      uuid,
      'modifyRootVolume',
    )
    try {
      await apiModifyRootVolume(uuid, root_volume_increment_size_gb, root_volume_throughput)

      setSuccess('Volume changed!')
    } catch (err: any) {
      setFailure(err)
      throw err
    }
  },
}

export const moduleMutations: MutationTree<WorkstationState> = {
  SET_WORKSTATION_REQUEST_INFO(
    state,
    { uuid, name, info }: { uuid: string; name: WorkstationRequestName; info: RequestInfo },
  ) {
    state.workstationsRequests[uuid][name] = info
  },
  CREATE_WORKSTATION_REQUEST_MAP(state, uuid) {
    state.workstationsRequests = {
      ...state.workstationsRequests,
      [uuid]: {
        startWorkstation: new RequestInfo(),
        stopWorkstation: new RequestInfo(),
        connectToWorkstationRDP: new RequestInfo(),
        connectToWorkstationTGX: new RequestInfo(),
        connectToWorkstationTeradici: new RequestInfo(),
        deleteWorkstation: new RequestInfo(),
        createWorkstation: new RequestInfo(),
        modifyRootVolume: new RequestInfo(),
      },
    }
  },
  SET_SHAREE_WORKSTATIONS(state, newShareeWorkstations) {
    state.shareeWorkstations = newShareeWorkstations
  },
  SET_USAGE_DATA(state, usage: WorkstationUsageTime) {
    state.usage = usage
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
}

const workstation: Module<WorkstationState, RootState> = {
  namespaced: true,
  // prefixed with "module-", so that they do not get shadowed by desctructured context
  state: moduleState,
  getters: moduleGetters,
  actions: moduleActions,
  mutations: moduleMutations,
}

export default workstation
